import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

interface TripPhoto {
  id: string;
  url: string;
  original_name: string;
}

interface Trip {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  country: string;
  location: string;
  date_from: string;
  date_to: string;
  status: "draft" | "published";
  is_active: boolean;
  is_featured: boolean;
  highlights: string[];
  includes: string[];
  excludes: string[];
  planning: { day: string; planning: string }[];
  photos: TripPhoto[];
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgPage: "#f4f6f8",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  draftBg: "#fef3c7",
  draftText: "#92400e",
  publishedBg: "#d1fae5",
  publishedText: "#065f46",
  hiddenBg: "#fef2f2",
  hiddenText: "#dc2626",
  featuredBg: "#fef9c3",
  featuredText: "#854d0e",
};

interface IconProps {
  size?: number;
  color?: string;
}

const iconBase = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

const SearchIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const XIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PencilIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <path d="M 6.496094 1 C 5.675781 1 5 1.675781 5 2.496094 L 5 3 L 2 3 L 2 4 L 3 4 L 3 12.5 C 3 13.328125 3.671875 14 4.5 14 L 10.5 14 C 11.328125 14 12 13.328125 12 12.5 L 12 4 L 13 4 L 13 3 L 10 3 L 10 2.496094 C 10 1.675781 9.324219 1 8.503906 1 Z M 6.496094 2 L 8.503906 2 C 8.785156 2 9 2.214844 9 2.496094 L 9 3 L 6 3 L 6 2.496094 C 6 2.214844 6.214844 2 6.496094 2 Z M 5 5 L 6 5 L 6 12 L 5 12 Z M 7 5 L 8 5 L 8 12 L 7 12 Z M 9 5 L 10 5 L 10 12 L 9 12 Z" />
  </svg>
);

const StarIcon: React.FC<IconProps & { filled?: boolean }> = ({ size = 16, color = "currentColor", filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ChevronLeftIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const PlusIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ImagePlaceholderIcon: React.FC<IconProps> = ({ size = 22, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const badgeStyle = (bg: string, text: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 500,
  background: bg,
  color: text,
});

const iconButtonStyle = (danger?: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 30,
  height: 30,
  borderRadius: 6,
  border: "none",
  background: "transparent",
  color: danger ? colors.danger : colors.textMuted,
  cursor: "pointer",
});

const togglePillStyle: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: 11,
  fontWeight: 500,
  border: `1px solid ${colors.border}`,
  borderRadius: 20,
  background: "#fff",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const ListTrip = () => {
  const navigate = useNavigate();

  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "published">("all");
  const [visibilityFilter, setVisibilityFilter] = useState<"all" | "active" | "inactive">("all");

  const [showModal, setShowModal] = useState(false);
  const [tripToDelete, setTripToDelete] = useState<Trip | null>(null);

  const fetchTrips = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/trips`, {
        params: {
          page,
          per_page: 10,
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(statusFilter !== "all" ? { status: statusFilter } : {}),
          ...(visibilityFilter !== "all" ? { visibility: visibilityFilter } : {}),
        },
      });
      setTrips(response.data.data);
      setCurrentPage(response.data.meta.current_page);
      setLastPage(response.data.meta.last_page);
      setTotal(response.data.meta.total);
    } catch (error) {
      console.error("Error fetching trips:", error);
      toast.error("Error fetching trips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchTrips(1), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, visibilityFilter]);

  const handleDeleteClick = (trip: Trip) => {
    setTripToDelete(trip);
    setShowModal(true);
  };

  const handleCancelDelete = () => {
    setShowModal(false);
    setTripToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!tripToDelete) return;
    try {
      await axios.delete(`${API_URL}/trips/${tripToDelete.id}`);
      setTrips((prev) => prev.filter((t) => t.id !== tripToDelete.id));
      setTotal((prev) => Math.max(0, prev - 1));
      toast.success("Trip deleted successfully");
    } catch (error: any) {
      console.error("Error deleting trip:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error deleting trip");
    } finally {
      setShowModal(false);
      setTripToDelete(null);
    }
  };

  const handleStatusChange = async (trip: Trip) => {
    const newStatus = trip.status === "published" ? "draft" : "published";
    try {
      const response = await axios.patch(`${API_URL}/trips/${trip.id}/status`, { status: newStatus });
      setTrips((prev) => prev.map((t) => (t.id === trip.id ? response.data : t)));
      toast.success(`Trip ${newStatus === "published" ? "published" : "moved to draft"} successfully`);
    } catch (error: any) {
      console.error("Error updating trip status:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error updating trip status");
    }
  };

  const handleVisibilityChange = async (trip: Trip) => {
    try {
      const response = await axios.patch(`${API_URL}/trips/${trip.id}/visibility`, {
        is_active: !trip.is_active,
      });
      setTrips((prev) => prev.map((t) => (t.id === trip.id ? response.data : t)));
      toast.success(`Trip ${!trip.is_active ? "shown" : "hidden"} on website successfully`);
    } catch (error: any) {
      console.error("Error updating trip visibility:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error updating trip visibility");
    }
  };

  const handleFeaturedChange = async (trip: Trip) => {
    try {
      const response = await axios.patch(`${API_URL}/trips/${trip.id}/featured`, {
        is_featured: !trip.is_featured,
      });
      setTrips((prev) => prev.map((t) => (t.id === trip.id ? response.data : t)));
      toast.success(!trip.is_featured ? "Trip added to featured" : "Trip removed from featured");
    } catch (error: any) {
      console.error("Error updating featured status:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error updating featured status");
    }
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setVisibilityFilter("all");
  };

  const hasActiveFilters = searchTerm !== "" || statusFilter !== "all" || visibilityFilter !== "all";

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: colors.bgPage,
          padding: "20px",
          boxSizing: "border-box",
          color: colors.text,
        }}
      >
        {/* Grille stricte : Left (20%), Center (60%), Right (20%) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "20% 60% 20%",
            gap: "16px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Section Gauche (20%) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div
              style={{
                background: "#fff",
                border: `0.5px solid ${colors.borderLight}`,
                borderRadius: 12,
                padding: 16,
              }}
            >
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 8px 0" }}>Manage tours</h2>
              <p style={{ fontSize: 13, color: colors.textFaint, margin: 0 }}>
                {total} {total === 1 ? "tour" : "tours"} total
              </p>
              <button
                type="button"
                onClick={() => navigate("/admin/add-tour")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  width: "100%",
                  marginTop: 16,
                  padding: "10px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: colors.primary,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.primaryHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = colors.primary)}
              >
                <PlusIcon size={14} color="#fff" />
                Add tour
              </button>
            </div>
          </div>

          {/* Section Centre (60%) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Table */}
            <div style={{ background: "#fff", border: `0.5px solid ${colors.borderLight}`, borderRadius: 12, overflow: "hidden" }}>
              {loading ? (
                <div style={{ padding: 40, textAlign: "center", fontSize: 14, color: colors.textFaint }}>
                  Loading tours...
                </div>
              ) : trips.length === 0 ? (
                <div style={{ padding: 48, textAlign: "center" }}>
                  <div style={{ fontSize: 14, color: colors.textMuted, marginBottom: hasActiveFilters ? 8 : 0 }}>
                    {hasActiveFilters ? "No tours match your filters." : "No tours yet."}
                  </div>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      style={{ background: "none", border: "none", color: colors.primary, cursor: "pointer", fontSize: 13, textDecoration: "underline" }}
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: colors.bgSubtle }}>
                        {["Tour", "Location", "Dates", "Price", "Status", ""].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "12px 14px",
                              fontSize: 12,
                              fontWeight: 500,
                              color: colors.textMuted,
                              textAlign: "left",
                              borderBottom: `0.5px solid ${colors.borderLight}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {trips.map((trip) => {
                        const photo = trip.photos?.[0];
                        return (
                          <tr key={trip.id} style={{ borderBottom: `0.5px solid ${colors.borderLight}` }}>
                            <td style={{ padding: 14, verticalAlign: "top" }}>
                              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                <div
                                  style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 8,
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    background: colors.bgSubtle,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: colors.textFaint,
                                  }}
                                >
                                  {photo ? (
                                    <img
                                      src={photo.url}
                                      alt={trip.title}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <ImagePlaceholderIcon />
                                  )}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                    <span style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{trip.title}</span>
                                    {trip.is_featured && (
                                      <span style={badgeStyle(colors.featuredBg, colors.featuredText)}>
                                        Featured
                                      </span>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: colors.textFaint,
                                      maxWidth: 180,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {trip.description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: 14, verticalAlign: "top" }}>
                              <div style={{ fontSize: 13, color: colors.text }}>{trip.location}</div>
                              <div style={{ fontSize: 12, color: colors.textFaint }}>{trip.country}</div>
                            </td>
                            <td style={{ padding: 14, verticalAlign: "top", fontSize: 12, color: colors.text }}>
                              {formatDate(trip.date_from)}
                              <div style={{ color: colors.textFaint, marginTop: 2 }}>to {formatDate(trip.date_to)}</div>
                            </td>
                            <td style={{ padding: 14, verticalAlign: "top", fontSize: 13, fontWeight: 500, color: colors.text, whiteSpace: "nowrap" }}>
                              {trip.currency} {trip.price}
                            </td>
                            <td style={{ padding: 14, verticalAlign: "top" }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                                {!trip.is_active ? (
                                  <span style={badgeStyle(colors.hiddenBg, colors.hiddenText)}>Hidden</span>
                                ) : trip.status === "published" ? (
                                  <span style={badgeStyle(colors.publishedBg, colors.publishedText)}>Published</span>
                                ) : (
                                  <span style={badgeStyle(colors.draftBg, colors.draftText)}>Draft</span>
                                )}
                                <div style={{ display: "flex", gap: 4 }}>
                                  <button type="button" onClick={() => handleStatusChange(trip)} style={togglePillStyle}>
                                    {trip.status === "published" ? "To draft" : "Publish"}
                                  </button>
                                  <button type="button" onClick={() => handleVisibilityChange(trip)} style={togglePillStyle}>
                                    {trip.is_active ? "Hide" : "Show"}
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: 14, verticalAlign: "top" }}>
                              <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                                <button
                                  type="button"
                                  title={trip.is_featured ? "Remove from featured" : "Mark as featured"}
                                  onClick={() => handleFeaturedChange(trip)}
                                  style={iconButtonStyle()}
                                  onMouseEnter={(e) => (e.currentTarget.style.color = colors.featuredText)}
                                  onMouseLeave={(e) => (e.currentTarget.style.color = colors.textMuted)}
                                >
                                  <StarIcon filled={trip.is_featured} color={trip.is_featured ? "#ca8a04" : "currentColor"} />
                                </button>
                                <button
                                  type="button"
                                  title="Edit"
                                  onClick={() => navigate(`/admin/edit-tour/${trip.id}`)}
                                  style={iconButtonStyle()}
                                  onMouseEnter={(e) => (e.currentTarget.style.color = colors.primary)}
                                  onMouseLeave={(e) => (e.currentTarget.style.color = colors.textMuted)}
                                >
                                  <PencilIcon />
                                </button>
                                <button
                                  type="button"
                                  title="Delete"
                                  onClick={() => handleDeleteClick(trip)}
                                  style={iconButtonStyle(true)}
                                >
                                  <TrashIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {!loading && trips.length > 0 && lastPage > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    borderTop: `0.5px solid ${colors.borderLight}`,
                    flexWrap: "wrap",
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: 12, color: colors.textFaint }}>
                    Page {currentPage} of {lastPage}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => fetchTrips(currentPage - 1)}
                      style={{
                        ...iconButtonStyle(),
                        border: `1px solid ${colors.border}`,
                        opacity: currentPage === 1 ? 0.4 : 1,
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      <ChevronLeftIcon size={14} />
                    </button>
                    {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
                      let page: number;
                      if (lastPage <= 5) page = i + 1;
                      else if (currentPage <= 3) page = i + 1;
                      else if (currentPage >= lastPage - 2) page = lastPage - 4 + i;
                      else page = currentPage - 2 + i;

                      const active = page === currentPage;
                      return (
                        <button
                          key={page}
                          type="button"
                          onClick={() => fetchTrips(page)}
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: 6,
                            border: `1px solid ${active ? colors.primary : colors.border}`,
                            background: active ? colors.primary : "#fff",
                            color: active ? "#fff" : colors.text,
                            fontSize: 12,
                            fontWeight: active ? 500 : 400,
                            cursor: "pointer",
                          }}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      disabled={currentPage === lastPage}
                      onClick={() => fetchTrips(currentPage + 1)}
                      style={{
                        ...iconButtonStyle(),
                        border: `1px solid ${colors.border}`,
                        opacity: currentPage === lastPage ? 0.4 : 1,
                        cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                      }}
                    >
                      <ChevronRightIcon size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section Droite (20%) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div
              style={{
                background: "#fff",
                border: `0.5px solid ${colors.borderLight}`,
                borderRadius: 12,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <h3 style={{ fontSize: 14, fontWeight: 500, margin: 0, color: colors.text }}>Filters</h3>

              {/* Search */}
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: colors.textFaint,
                    display: "flex",
                  }}
                >
                  <SearchIcon size={14} />
                </span>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "8px 28px 8px 30px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                    outline: "none",
                    background: colors.bgSubtle,
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => setSearchTerm("")}
                    style={{
                      position: "absolute",
                      right: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: colors.textFaint,
                      cursor: "pointer",
                      display: "flex",
                      padding: 2,
                    }}
                  >
                    <XIcon size={12} />
                  </button>
                )}
              </div>

              {/* Select Status */}
              <div>
                <label style={{ fontSize: 12, color: colors.textMuted, display: "block", marginBottom: 4 }}>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "8px 10px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <option value="all">All status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>

              {/* Select Visibility */}
              <div>
                <label style={{ fontSize: 12, color: colors.textMuted, display: "block", marginBottom: 4 }}>Visibility</label>
                <select
                  value={visibilityFilter}
                  onChange={(e) => setVisibilityFilter(e.target.value as any)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "8px 10px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <option value="all">All visibility</option>
                  <option value="active">Visible</option>
                  <option value="inactive">Hidden</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  style={{
                    marginTop: 4,
                    background: "none",
                    border: "none",
                    color: colors.primary,
                    cursor: "pointer",
                    fontSize: 12,
                    textAlign: "left",
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Reset filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de suppression */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div style={{ background: "#fff", borderRadius: 12, width: 420, maxWidth: "100%", overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 20px",
                borderBottom: `0.5px solid ${colors.borderLight}`,
              }}
            >
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500, color: colors.text }}>Delete tour</h3>
              <button
                type="button"
                onClick={handleCancelDelete}
                aria-label="Close"
                style={{ background: "none", border: "none", color: colors.textFaint, cursor: "pointer", display: "flex" }}
              >
                <XIcon size={18} />
              </button>
            </div>
            <div style={{ padding: 20, fontSize: 13, color: colors.textMuted, lineHeight: 1.6 }}>
              Are you sure you want to delete{" "}
              <strong style={{ color: colors.text }}>{tripToDelete?.title}</strong>? This can't be undone.
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                padding: "14px 20px",
                borderTop: `0.5px solid ${colors.borderLight}`,
              }}
            >
              <button
                type="button"
                onClick={handleCancelDelete}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  background: "#fff",
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: colors.danger,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListTrip;